import React, { useState, useEffect } from "react";

const NotificationPopup = ({ onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [cycloneAlerts, setCycloneAlerts] = useState([]);
  const [visible, setVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("risk");
  const [riskTabTitle, setRiskTabTitle] = useState("🌧️ Weather Risk Alerts");
  const [userContext, setUserContext] = useState({ type: 'general', location: '' });

  // Get user context from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (user.selectedState && user.selectedDistrict) {
          setUserContext({
            type: 'district',
            state: user.selectedState,
            district: user.selectedDistrict,
            location: `${user.selectedDistrict}, ${user.selectedState}`
          });
        } else if (user.selectedState) {
          setUserContext({
            type: 'state',
            state: user.selectedState,
            location: user.selectedState
          });
        } else {
          setUserContext({ type: 'general', location: 'All India' });
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        setUserContext({ type: 'general', location: 'All India' });
      }
    } else {
      setUserContext({ type: 'general', location: 'All India' });
    }
  }, []);

  useEffect(() => {
    const fetchRiskData = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001"))}/api/disaster-notifications`);
        const json = await res.json();
        const states = json.data.states || [];
        
        // Filter notifications based on user context
        let filteredStates = [];
        
        if (userContext.type === 'state' && userContext.state) {
          // Show only notifications for the user's state - even if no alerts
          const userStateData = states.find(state => state.state === userContext.state);
          if (userStateData) {
            filteredStates = [userStateData];
          } else {
            // Create empty state data if not found in API response
            filteredStates = [{
              state: userContext.state,
              floodRisk: 'Low',
              droughtRisk: 'Low',
              weatherAlerts: [],
              riskFactors: {},
              totalRiskScore: 0,
              overallRisk: 'Low',
              lastUpdated: new Date().toISOString(),
              dataQuality: { hasWeatherData: false, alertCount: 0 }
            }];
          }
        } else if (userContext.type === 'district' && userContext.state) {
          // Show only notifications for the user's state - even if no alerts
          const userStateData = states.find(state => state.state === userContext.state);
          if (userStateData) {
            filteredStates = [userStateData];
          } else {
            // Create empty state data if not found in API response
            filteredStates = [{
              state: userContext.state,
              floodRisk: 'Low',
              droughtRisk: 'Low',
              weatherAlerts: [],
              riskFactors: {},
              totalRiskScore: 0,
              overallRisk: 'Low',
              lastUpdated: new Date().toISOString(),
              dataQuality: { hasWeatherData: false, alertCount: 0 }
            }];
          }
        } else {
          // For general users, show top risk states across India
          filteredStates = states;
        }

        const scoredStates = filteredStates.map((state) => {
          const floodScore = state.floodRisk === "High" ? 2 : state.floodRisk === "Medium" ? 1 : 0;
          const droughtScore =
            state.droughtRisk === "High" ? 2 : state.droughtRisk === "Medium" ? 1 : 0;
          const recentDisastersCount = Object.values(state.recentDisasters || {}).reduce(
            (sum, d) => sum + (d.count || 0),
            0
          );
          return { ...state, riskScore: floodScore + droughtScore + recentDisastersCount };
        });

        let topRiskStates;
        if (userContext.type === 'general') {
          // For general users, show top 5 risk states
          topRiskStates = scoredStates
            .sort((a, b) => b.riskScore - a.riskScore)
            .slice(0, 5);
        } else {
          // For state/district users, show only their state (even if no alerts)
          topRiskStates = scoredStates;
        }

        setNotifications(topRiskStates);
        
        // Update title based on user context and alert types
        const hasRainfall = topRiskStates.some(state => 
          state.floodRisk === 'High' || state.floodRisk === 'Medium' ||
          (state.weatherAlerts && state.weatherAlerts.some(alert => 
            alert.description.toLowerCase().includes('rain') || 
            alert.description.toLowerCase().includes('flood')
          ))
        );
        
        const hasDrought = topRiskStates.some(state => 
          state.droughtRisk === 'High' || state.droughtRisk === 'Medium'
        );
        
        const hasCold = topRiskStates.some(state => 
          state.weatherAlerts && state.weatherAlerts.some(alert => 
            alert.description.toLowerCase().includes('cold')
          )
        );
        
        const hasHeat = topRiskStates.some(state => 
          state.weatherAlerts && state.weatherAlerts.some(alert => 
            alert.description.toLowerCase().includes('heat')
          )
        );
        
        let sortedStates = [...topRiskStates];
        
        if (hasCold) {
          sortedStates.sort((a, b) => {
            const aTempMin = a.riskFactors?.currentTemp || 100;
            const bTempMin = b.riskFactors?.currentTemp || 100;
            return aTempMin - bTempMin;
          });
        } else if (hasHeat) {
          sortedStates.sort((a, b) => {
            const aTempMax = a.riskFactors?.currentTemp || 0;
            const bTempMax = b.riskFactors?.currentTemp || 0;
            return bTempMax - aTempMax;
          });
        } else if (hasRainfall) {
          sortedStates.sort((a, b) => {
            const aPrecip = a.riskFactors?.totalPrecip || 0;
            const bPrecip = b.riskFactors?.totalPrecip || 0;
            return bPrecip - aPrecip;
          });
        }
        
        setNotifications(sortedStates);
        
        // Set title based on user context and alert types
        let titlePrefix = '';
        if (userContext.type === 'state') {
          titlePrefix = `🏛️ ${userContext.state} - `;
        } else if (userContext.type === 'district') {
          titlePrefix = `🏢 ${userContext.district} - `;
        }
        
        // For state/district users, always show their location in title regardless of alerts
        if (userContext.type !== 'general') {
          if (topRiskStates.length > 0 && topRiskStates[0].weatherAlerts && topRiskStates[0].weatherAlerts.length > 0) {
            // Has alerts - categorize them
            if (hasRainfall && hasDrought) {
              setRiskTabTitle(`${titlePrefix}🌦️ Weather Risk Alerts`);
            } else if (hasRainfall) {
              setRiskTabTitle(`${titlePrefix}�️ Rainfall Risk Alerts`);
            } else if (hasDrought) {
              setRiskTabTitle(`${titlePrefix}☀️ Drought Risk Alerts`);
            } else if (hasCold && hasHeat) {
              setRiskTabTitle(`${titlePrefix}🌡️ Temperature Alerts`);
            } else if (hasCold) {
              setRiskTabTitle(`${titlePrefix}❄️ Cold Wave Alerts`);
            } else if (hasHeat) {
              setRiskTabTitle(`${titlePrefix}🔥 Heat Wave Alerts`);
            } else {
              setRiskTabTitle(`${titlePrefix}⚠️ Weather Alerts`);
            }
          } else {
            // No alerts - show status
            setRiskTabTitle(`${titlePrefix}✅ Weather Status`);
          }
        } else {
          // General users - show national alerts
          if (hasRainfall && hasDrought) {
            setRiskTabTitle("🌦️ Weather Risk States");
          } else if (hasRainfall) {
            setRiskTabTitle("🌧️ Rainfall Risk States");
          } else if (hasDrought) {
            setRiskTabTitle("☀️ Drought Risk States");
          } else if (hasCold && hasHeat) {
            setRiskTabTitle("🌡️ Temperature Alert States");
          } else if (hasCold) {
            setRiskTabTitle("❄️ Cold Wave Alert States");
          } else if (hasHeat) {
            setRiskTabTitle("🔥 Heat Wave Alert States");
          } else {
            setRiskTabTitle("⚠️ Weather Alert States");
          }
        }
      } catch (err) {
        console.error("Error fetching risk notifications:", err);
      }
    };

    const fetchCycloneData = async () => {
      try {
        const proxyUrl = 'https://api.allorigins.win/raw?url=';
        const targetUrl = 'https://cap-sources.s3.amazonaws.com/in-imd-en/rss.xml';
        
        const res = await fetch(proxyUrl + encodeURIComponent(targetUrl));
        const xmlText = await res.text();
        
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, "text/xml");
        const items = xmlDoc.getElementsByTagName("item");
        
        const cycloneData = [];
        for (let i = 0; i < Math.min(items.length, 10); i++) {
          const item = items[i];
          const title = item.getElementsByTagName("title")[0]?.textContent || "";
          const description = item.getElementsByTagName("description")[0]?.textContent || "";
          const pubDate = item.getElementsByTagName("pubDate")[0]?.textContent || "";
          
          if (title.toLowerCase().includes("cyclone") || 
              description.toLowerCase().includes("cyclone") ||
              title.toLowerCase().includes("storm") ||
              description.toLowerCase().includes("storm")) {
            
            const location = extractLocation(title + " " + description);
            
            // Filter cyclone alerts based on user context
            let shouldInclude = true;
            if (userContext.type === 'state' && userContext.state) {
              shouldInclude = location.toLowerCase().includes(userContext.state.toLowerCase());
            } else if (userContext.type === 'district' && userContext.state) {
              shouldInclude = location.toLowerCase().includes(userContext.state.toLowerCase()) ||
                             location.toLowerCase().includes(userContext.district.toLowerCase());
            }
            
            if (shouldInclude) {
              cycloneData.push({
                title,
                description: description.substring(0, 200) + "...",
                location,
                date: new Date(pubDate).toLocaleDateString(),
                severity: getSeverity(title + " " + description)
              });
            }
          }
        }
        
        setCycloneAlerts(cycloneData);
      } catch (err) {
        console.error("Error fetching cyclone data:", err);
        // Provide fallback data only if it matches user context
        if (userContext.type === 'general' || 
            (userContext.state && ['Odisha', 'West Bengal', 'Andhra Pradesh', 'Tamil Nadu'].includes(userContext.state))) {
          setCycloneAlerts([
            {
              title: "Cyclone Alert - Bay of Bengal",
              description: "A low pressure area over Bay of Bengal is likely to intensify into a cyclonic storm...",
              location: "Odisha, West Bengal",
              date: new Date().toLocaleDateString(),
              severity: "Medium"
            }
          ]);
        } else {
          setCycloneAlerts([]);
        }
      }
    };

    const fetchData = async () => {
      await Promise.all([fetchRiskData(), fetchCycloneData()]);
      setVisible(true);
    };

    fetchData();
    const interval = setInterval(fetchData, 1000 * 60 * 60);
    return () => clearInterval(interval);
  }, [userContext.type, userContext.state, userContext.district]);

  const extractLocation = (text) => {
    const indianStates = [
      "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", 
      "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", 
      "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", 
      "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", 
      "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"
    ];
    
    const foundStates = indianStates.filter(state => 
      text.toLowerCase().includes(state.toLowerCase())
    );
    
    return foundStates.length > 0 ? foundStates.join(", ") : "Multiple regions";
  };

  const getSeverity = (text) => {
    const lowerText = text.toLowerCase();
    if (lowerText.includes("severe") || lowerText.includes("very severe") || lowerText.includes("extremely severe")) {
      return "High";
    } else if (lowerText.includes("cyclonic storm") || lowerText.includes("deep depression")) {
      return "Medium";
    }
    return "Low";
  };

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Dismiss button clicked!');
    
    if (onClose) {
      onClose();
    }
    
    setVisible(false);
  };

  if (!visible) return null;

  const getRiskLevel = (state) => {
    return state.overallRisk || 'Medium';
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'High': return "#E53935";
      case 'Medium': return "#D32F2F";
      case 'Low': return "#F44336";
      default: return "#E53935";
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>
          ⚠️ Weather & Disaster Alerts
          {userContext.type !== 'general' && (
            <span style={styles.locationBadge}>
              {userContext.type === 'state' ? '🏛️' : '🏢'} {userContext.location}
            </span>
          )}
        </h3>
        <small style={styles.subtitle}>
          {userContext.type === 'general' 
            ? 'Real-time notifications for India' 
            : `Localized alerts for ${userContext.location}`
          }
        </small>
        
        <div style={styles.tabContainer}>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === "risk" ? styles.activeTab : {})
            }}
            onClick={() => setActiveTab("risk")}
          >
            {riskTabTitle}
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === "cyclone" ? styles.activeTab : {})
            }}
            onClick={() => setActiveTab("cyclone")}
          >
            🌀 Cyclone Alerts
          </button>
        </div>
      </div>

      {activeTab === "risk" && (
        <>
          {notifications.length === 0 || (notifications.length > 0 && notifications[0].weatherAlerts && notifications[0].weatherAlerts.length === 0 && notifications[0].floodRisk === 'Low' && notifications[0].droughtRisk === 'Low') ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>✅</div>
              <p style={styles.emptyTitle}>All Clear!</p>
              <p style={styles.emptyText}>
                {userContext.type === 'general' 
                  ? 'No states currently under severe weather alert.\nAll regions are experiencing normal weather conditions.'
                  : `No severe weather alerts for ${userContext.location}.\nYour region is experiencing normal weather conditions.`
                }
              </p>
            </div>
          ) : (
            notifications.map((state) => {
              const riskLevel = getRiskLevel(state);
              const riskColor = getRiskColor(riskLevel);
              return (
                <div
                  key={state.state}
                  style={{
                    ...styles.card,
                    borderLeft: `6px solid ${riskColor}`,
                    backgroundColor: `${riskColor}15`,
                  }}
                >
                  <div style={styles.cardHeader}>
                    <h4 style={styles.stateName}>
                      {typeof state.state === 'string' ? 
                        state.state.replace(/[0-9]/g, '').trim() : 
                        'Unknown State'
                      }
                    </h4>
                    <div style={styles.riskContainer}>
                      {riskLevel !== 'Low' && (
                        <span
                          style={{
                            ...styles.riskTag,
                            backgroundColor: riskColor,
                          }}
                        >
                          {riskLevel} Risk
                        </span>
                      )}
                      {state.totalRiskScore && state.totalRiskScore > 0 && (
                        <small style={styles.riskScore}>
                          Score: {state.totalRiskScore.toFixed(1)}
                        </small>
                      )}
                    </div>
                  </div>

                  {(state.floodRisk !== 'Low' || state.droughtRisk !== 'Low' || state.weatherAlerts?.length > 0) && 
                   state.riskFactors && Object.keys(state.riskFactors).length > 0 && (
                    <div style={styles.section}>
                      <strong style={styles.sectionTitle}>🌡️ Current Conditions:</strong>
                      <div style={styles.riskFactorsGrid}>
                        {state.riskFactors.currentTemp && (
                          <span style={styles.factorItem}>🌡️ {state.riskFactors.currentTemp}°C</span>
                        )}
                        {state.riskFactors.currentHumidity && (
                          <span style={styles.factorItem}>💧 {state.riskFactors.currentHumidity}%</span>
                        )}
                        {state.riskFactors.totalPrecip !== undefined && state.riskFactors.totalPrecip > 0 && (
                          <span style={styles.factorItem}>🌧️ {state.riskFactors.totalPrecip}mm (7d)</span>
                        )}
                        {state.riskFactors.currentWindSpeed && state.riskFactors.currentWindSpeed > 20 && (
                          <span style={styles.factorItem}>💨 {state.riskFactors.currentWindSpeed} km/h</span>
                        )}
                      </div>
                    </div>
                  )}

                  {state.floodRisk !== 'Low' && (
                    <div style={styles.section}>
                      <strong style={styles.sectionTitle}>🌧️ Rainfall Risk:</strong>
                      <div style={styles.riskGrid}>
                        <span style={{
                          ...styles.riskBadge,
                          backgroundColor: '#E53935'
                        }}>
                          🌊 Flood Risk: {state.floodRisk}
                        </span>
                      </div>
                    </div>
                  )}

                  {state.recentDisasters &&
                    Object.keys(state.recentDisasters).length > 0 && (
                      <div style={styles.section}>
                        <strong style={styles.sectionTitle}>Recent Disasters:</strong>
                        {Object.entries(state.recentDisasters).map(([type, data]) => (
                          <div key={type} style={styles.detailRow}>
                            <span style={styles.detailText}>• {type} ({data.count})</span>
                            <small style={styles.detailDate}>
                              Last: {new Date(data.latestDate).toLocaleDateString()}
                            </small>
                          </div>
                        ))}
                      </div>
                    )}

                  {state.weatherAlerts && state.weatherAlerts.length > 0 ? (
                    <div style={styles.section}>
                      <strong style={styles.sectionTitle}>🌦️ Weather Alerts:</strong>
                      {state.weatherAlerts.slice(0, 3).map((alert, idx) => (
                        <div key={idx} style={styles.detailRow}>
                          <span style={{
                            ...styles.detailText,
                            color: alert.severity === 'extreme' ? '#B71C1C' : 
                                   alert.severity === 'high' ? '#E53935' : '#F44336'
                          }}>
                            • {alert.description}
                          </span>
                          <small style={styles.detailDate}>
                            {new Date(alert.date).toLocaleDateString()}
                          </small>
                        </div>
                      ))}
                      {state.weatherAlerts.length > 3 && (
                        <small style={styles.moreText}>+ {state.weatherAlerts.length - 3} more alerts...</small>
                      )}
                    </div>
                  ) : (
                    <div style={styles.section}>
                      <small style={{...styles.noAlerts, color: '#90A4AE'}}>
                        ℹ️ No severe weather alerts - Normal conditions
                      </small>
                    </div>
                  )}

                  {/* Only show data quality if there are actual alerts or risks */}
                  {(state.weatherAlerts?.length > 0 || state.totalRiskScore > 0 || Object.keys(state.recentDisasters || {}).length > 0) && 
                   state.dataQuality && (
                    <div style={styles.dataQuality}>
                      <small style={styles.dataQualityText}>
                        📊 Sources: {state.dataQuality.hasWeatherData ? '✅' : '❌'} Weather
                        {state.dataQuality.hasDisasterData && ', ✅ Disasters'}
                        {state.lastUpdated && (
                          <span> • {new Date(state.lastUpdated).toLocaleTimeString()}</span>
                        )}
                      </small>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </>
      )}

      {activeTab === "cyclone" && (
        <>
          {cycloneAlerts.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>🌀</div>
              <p style={styles.emptyTitle}>No Cyclone Alerts</p>
              <p style={styles.emptyText}>
                {userContext.type === 'general'
                  ? 'No cyclonic activity detected in Indian coastal regions.'
                  : `No cyclone alerts affecting ${userContext.location}.`
                }
              </p>
            </div>
          ) : (
            cycloneAlerts.map((alert, index) => {
              const severityColor = alert.severity === "High" ? "#E53935" : 
                                   alert.severity === "Medium" ? "#FB8C00" : "#43A047";
              return (
                <div
                  key={index}
                  style={{
                    ...styles.card,
                    borderLeft: `6px solid ${severityColor}`,
                    backgroundColor: `${severityColor}15`,
                  }}
                >
                  <div style={styles.cardHeader}>
                    <h4 style={styles.stateName}>{alert.title}</h4>
                    <span
                      style={{
                        ...styles.riskTag,
                        backgroundColor: severityColor,
                      }}
                    >
                      {alert.severity}
                    </span>
                  </div>
                  
                  <div style={styles.section}>
                    <strong style={styles.sectionTitle}>📍 Location:</strong>
                    <p style={styles.detailText}>{alert.location}</p>
                  </div>
                  
                  <div style={styles.section}>
                    <strong style={styles.sectionTitle}>📋 Details:</strong>
                    <p style={styles.detailText}>{alert.description}</p>
                  </div>
                  
                  <div style={styles.section}>
                    <small style={styles.detailDate}>Updated: {alert.date}</small>
                  </div>
                </div>
              );
            })
          )}
        </>
      )}

      <div style={styles.footer}>
        <button 
          style={styles.dismissButton} 
          onClick={handleClose}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#e0e0e0';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#f5f5f5';
          }}
        >
          ✖ Click to dismiss
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: {
    position: "fixed",
    top: 20,
    right: 20,
    background: "#FFFFFF",
    padding: "1rem",
    borderRadius: "14px",
    width: "min(420px, calc(100vw - 24px))",
    maxHeight: "85vh",
    overflowY: "auto",
    boxShadow: "0 8px 25px rgba(0,0,0,0.18)",
    zIndex: 99999,
    animation: "fadeIn 0.3s ease-in-out",
    fontFamily: "Inter, Arial, sans-serif",
    pointerEvents: "auto",
  },
  header: {
    textAlign: "center",
    paddingBottom: "8px",
    marginBottom: "12px",
    borderBottom: "1px solid #E0E0E0",
  },
  title: {
    margin: 0,
    color: "#E53935",
    fontSize: "18px",
    fontWeight: "600",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
  },
  locationBadge: {
    fontSize: "12px",
    fontWeight: "500",
    color: "#10b981",
    backgroundColor: "rgba(16, 185, 129, 0.1)",
    padding: "2px 8px",
    borderRadius: "12px",
    border: "1px solid rgba(16, 185, 129, 0.2)",
  },
  subtitle: {
    color: "#607D8B",
    fontSize: "12px",
    marginBottom: "12px",
  },
  tabContainer: {
    display: "flex",
    gap: "4px",
    marginTop: "10px",
    flexWrap: "wrap",
  },
  tab: {
    flex: "1 1 auto",
    minWidth: "120px",
    padding: "8px 12px",
    border: "1px solid #ddd",
    background: "#f8f9fa",
    color: "#666",
    fontSize: "12px",
    fontWeight: "500",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    outline: "none",
  },
  activeTab: {
    background: "#E53935",
    color: "#fff",
    borderColor: "#E53935",
  },
  card: {
    borderRadius: "10px",
    padding: "12px",
    marginBottom: "12px",
    transition: "transform 0.2s ease",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "6px",
  },
  riskContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "2px",
  },
  riskScore: {
    color: "#78909C",
    fontSize: "10px",
    fontWeight: "500",
  },
  riskFactorsGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    marginTop: "4px",
  },
  factorItem: {
    fontSize: "11px",
    backgroundColor: "#f0f0f0",
    padding: "2px 6px",
    borderRadius: "4px",
    color: "#555",
  },
  riskGrid: {
    display: "flex",
    gap: "6px",
    marginTop: "4px",
    flexWrap: "wrap",
  },
  riskBadge: {
    fontSize: "11px",
    padding: "3px 8px",
    borderRadius: "12px",
    color: "white",
    fontWeight: "600",
  },
  stateName: {
    margin: 0,
    fontWeight: "600",
    fontSize: "16px",
    color: "#263238",
  },
  riskTag: {
    fontSize: "12px",
    padding: "3px 8px",
    borderRadius: "20px",
    color: "#FFFFFF",
    fontWeight: "600",
  },
  section: {
    marginTop: "6px",
  },
  sectionTitle: {
    color: "#E53935",
    fontSize: "14px",
    fontWeight: "600",
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "13px",
    margin: "3px 0",
  },
  detailText: {
    color: "#263238",
    fontWeight: "500",
    fontSize: "13px",
    lineHeight: "1.4",
  },
  detailDate: {
    color: "#78909C",
    fontSize: "12px",
  },
  moreText: {
    color: "#90A4AE",
    fontSize: "12px",
  },
  noAlerts: {
    color: "#90A4AE",
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#607D8B",
  },
  emptyIcon: {
    fontSize: "48px",
    marginBottom: "16px",
  },
  emptyTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#37474F",
    marginBottom: "8px",
  },
  emptyText: {
    fontSize: "14px",
    color: "#78909C",
    lineHeight: "1.6",
  },
  footer: {
    textAlign: "center",
    marginTop: "12px",
    padding: "6px 0",
  },
  dismissButton: {
    background: "#f5f5f5",
    border: "2px solid #ddd",
    color: "#546E7A",
    fontSize: "14px",
    cursor: "pointer",
    padding: "10px 20px",
    borderRadius: "8px",
    transition: "all 0.2s ease",
    fontWeight: "600",
    minWidth: "150px",
    outline: "none",
    userSelect: "none",
  },
  dataQuality: {
    marginTop: "8px",
    padding: "4px 0",
    borderTop: "1px solid #f0f0f0",
  },
  dataQualityText: {
    fontSize: "10px",
    color: "#90A4AE",
  },
};

export default NotificationPopup;